import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Text } from '@blueprint/ui';
import { ZooplaLogo } from '@blueprint/logo';
import { UserSmall } from '@blueprint/icons';

import {
    ResetButtonLink,
    ResetLink,
    MainHeader,
    MaxWidthContainer
} from '../../Header.styles';
import { ProfileMenuWrapper } from '../ProfileMenu/ProfileMenu.styles';

const LogoWrapper = styled('div')(
    (props) => css`
        display: flex;
        width: 100%;
        height: auto;
        padding-right: 0; // for the hamburger button
        align-items: center;
        // need the !important because old stacks reset it with !important
        box-shadow: 0px 0px 8px rgba(50, 39, 68, 0.04),
            0px 0px 1px rgba(50, 39, 68, 0.32),
            0px 8px 8px -8px rgba(50, 39, 68, 0.16) !important;
        z-index: 2;
        position: relative;

        ${props.theme.mediaQueries.large} {
            top: auto;
            left: auto;
            width: auto;
            height: auto;
            box-shadow: none !important;
            z-index: auto;
            padding-right: 0;
        }

        @media (min-width: 1400px) {
            height: 72px;
        }

        div {
            margin-left: auto;
        }

        ${ResetLink} {
            display: flex;
            padding: 14px ${props.theme.spacing['16px']};
            padding-right: 4px;
            border-radius: ${props.theme.borderRadius['4px']};

            &:focus,
            &:hover {
                text-decoration: none;
                transform: translateY(0);
            }

            &:focus {
                box-shadow: inset ${props.theme.focusRing} !important;
            }
        }

        ${ProfileMenuWrapper} {
            display: none;

            ${props.theme.mediaQueries.large} {
                display: inline-block;
                margin-left: auto;
            }
        }
    `
);

const MobileActionsWrapper = styled('div')(
    (props) => css`
        display: flex;
        width: 100%;

        ${props.theme.mediaQueries.large} {
            display: none;
        }

        [data-testid='header']:not([data-is-logged-in])
            &
            [data-is-logged-in='true'] {
            display: none;
        }

        [data-is-logged-in] & [data-is-logged-in='false'] {
            display: none;
        }
    `
);

const MobileLinksWrapper = styled('div')(
    () => css`
        display: flex;
    `
);

const StyledUserIcon = styled(UserSmall)(
    (props) =>
        css`
            fill: ${props.theme.colors['primary-brand']} !important;
        `
);

const HamburgerSpan = styled('span')(
    (props) =>
        css`
            display: block;
            width: 100%;
            height: 1px;
            background: ${props.theme.colors['icon-dark']};
            border-radius: ${props.theme.spacing['4px']};
            transition: all 0.5s cubic-bezier(0.77, 0.2, 0.05, 1);

            &:nth-of-type(2) {
                margin: 5px 0;
            }
        `
);

const HamburgerWrapper = styled('div')`
    display: flex;
    flex-flow: column wrap;
    justify-content: center;
`;

const ActionButton = styled(ResetButtonLink)(
    (props) => css`
        display: block;
        appearance: none;
        border: 0;
        background: none;
        top: 2px;
        right: 2px;
        width: ${props.theme.spacing['48px']};
        height: ${props.theme.spacing['48px']};
        padding: 0 14px;
        cursor: pointer;
        border-radius: ${props.theme.spacing['4px']};
        text-decoration: none;
        z-index: 2;

        &:focus {
            outline: 0;
            box-shadow: ${props.theme.focusRing} !important;
        }

        &:hover {
            background-color: rgba(102, 45, 190, 0.1);

            // we need this specific selector for this rule to be in the right specificity
            &:not(:active) {
                text-decoration: none;
                transform: none;
                box-shadow: none;
            }
        }

        ${props.theme.mediaQueries.large} {
            display: none;
        }

        > * {
            pointer-events: none;
        }
    `
);

export const ActionWithLabelButton = styled(ActionButton)<{
    'variation-hidden'?: string;
}>(
    () => css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 10px 0.5rem 0;
        min-width: 48px;
        width: auto;

        ${Text} {
            margin-top: 3px;
            font-size: 12px;
            font-weight: 600;
        }

        svg {
            overflow: visible;
        }

        @media only screen and (max-width: 359px) {
            ${Text} {
                font-size: 10px;
            }
        }
    `
);

const MenuButton = styled(ActionWithLabelButton)(
    (props) => css`
        justify-content: flex-end;

        // increase specificity to override old stack
        ${MainHeader} button& {
            width: auto;

            &:hover {
                background-color: rgba(102, 45, 190, 0.1);
            }
        }

        [data-id='close-button'] {
            display: none;
        }

        ${Text} {
            margin-top: 0px;
        }

        @media only screen and (max-width: 359px) {
            ${Text} {
                margin-top: 1px;
            }
        }

        ${HamburgerWrapper} {
            width: calc(3rem - 28px);
            padding: 0;
            margin: 0 auto;
            min-height: 20px;
        }

        ${props.theme.mediaQueries.large} {
            display: none;
        }

        // the menu button is an actual <button> so we need to reference it for this rule to work
        ${MainHeader}[data-universal-header-mobile-menu-open] ${MaxWidthContainer} button& {
            ${HamburgerSpan}:nth-of-type(1) {
                transform: rotate(45deg);
            }
            ${HamburgerSpan}:nth-of-type(2) {
                opacity: 0;
                margin: -1px 0;
            }
            ${HamburgerSpan}:nth-of-type(3) {
                transform: rotate(-45deg);
            }
        }

        &[aria-expanded='true'] {
            [data-id='open-button'] {
                display: none;
            }

            [data-id='close-button'] {
                display: block;
            }
        }
    `
);

const StyledZooplaLogo = styled(ZooplaLogo)(
    (props) => `
        fill: ${props.theme.colors['primary-brand']};
        width: 88px;
        height: 25px;

        ${props.theme.mediaQueries.large} {
            width: 110px;
            height: 32px;
        }
`
);

export {
    LogoWrapper,
    MenuButton,
    HamburgerSpan,
    HamburgerWrapper,
    StyledZooplaLogo,
    MobileActionsWrapper,
    MobileLinksWrapper,
    StyledUserIcon
};
